import { buildGraph, kinship, parseBackup, sampleBackup } from '@lineage/core';
import { Link } from '@tanstack/react-router';
import { samplePortraitUrl } from '../canvas/samplePortraits';
import { useEffect, useMemo, useRef, useState } from 'react';
import s from './Landing.module.css';
import { useKinPack } from '../../lib/prefs';
import { useT } from '../../i18n';

/** The slice of the sample family shown in the hero: x/y are percentages of the stage. */
const SPOTS = [
  { id: 'thomas', x: 36, y: 15, tone: 0 },
  { id: 'helen', x: 64, y: 15, tone: 1 },
  { id: 'lucy', x: 13, y: 50, tone: 2 },
  { id: 'daniel', x: 40, y: 50, tone: 3 },
  { id: 'priya', x: 68, y: 50, tone: 4 },
  { id: 'leo', x: 54, y: 85, tone: 5 },
] as const;

const VIEWERS = ['daniel', 'priya', 'lucy', 'leo'] as const;

/** Reveals sections as they scroll into view; content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const items = el.querySelectorAll<HTMLElement>('[data-reveal]');
    items.forEach((i) => i.classList.add(s.pre!));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.remove(s.pre!);
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    items.forEach((i) => io.observe(i));
    return () => io.disconnect();
  }, []);
  return ref;
}

export function Mark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round">
        <path d="M32 22v12M32 34H18v8M32 34h14v8" />
      </g>
      <g fill="currentColor">
        <circle cx="32" cy="18" r="7" />
        <circle cx="18" cy="46" r="7" />
        <circle cx="46" cy="46" r="7" />
      </g>
    </svg>
  );
}

/** Interactive sample tree; `compact` drops the header and caption and shortens the stage (app onboarding). */
export function LiveTree({ compact = false }: { compact?: boolean }) {
  const { graph, names, ids } = useMemo(() => {
    const data = parseBackup(sampleBackup());
    const g = buildGraph(data.people, data.parentLinks, data.partnerships);
    // The importer assigns fresh ids; idMap maps the sample's readable ids ('daniel') to them.
    const byReal = new Map(data.people.map((p) => [p.id, p.firstName]));
    const idOf = (key: string) => data.idMap[key]!;
    return { graph: g, names: new Map(Object.keys(data.idMap).map((k) => [k, byReal.get(idOf(k)) ?? k])), ids: idOf };
  }, []);
  const [meId, setMeId] = useState<string>('daniel');
  const pack = useKinPack();
  const seeAs = useT().profile.seeAs;
  const label = (key: string) => kinship(graph, ids(meId), ids(key), pack);
  const meName = names.get(meId) ?? '';

  return (
    <div className={`${s.treeWrap} ${compact ? s.compact : ''}`}>
      {!compact && <div className={s.treeHead}>
        <span className="eyebrow">Live sample · The Whitfields</span>
        <span className={s.treeHint}>Tap anyone to see the tree through their eyes</span>
      </div>}
      <div className={s.stage}>
        <svg className={s.lines} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <path d="M36 15H64M50 15V32M13 32H40M13 32V50M40 32V50M40 50H68M54 50V85" vectorEffect="non-scaling-stroke" />
        </svg>
        {SPOTS.map((p) => {
          const name = names.get(p.id) ?? p.id;
          const isMe = p.id === meId;
          return (
            <button
              key={p.id}
              type="button"
              className={`${s.person} ${isMe ? s.me : ''}`}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
              aria-pressed={isMe}
              aria-label={`${name}, ${label(p.id)}. ${seeAs(name)}`}
              onClick={() => setMeId(p.id)}
            >
              <span className={s.avatar} style={{ background: `var(--p${p.tone})` }}>
                {name[0]}
                <img src={samplePortraitUrl(p.id)} alt="" draggable={false} onError={(e) => e.currentTarget.remove()} />
              </span>
              <span className={s.pname}>{name}</span>
              <span className={`${s.rel} ${isMe ? s.relMe : ''}`} key={`${p.id}-${meId}`} lang={pack.lang} dir={pack.dir}>{label(p.id)}</span>
            </button>
          );
        })}
      </div>
      <div className={s.chips} role="group" aria-label="See the tree as">
        <span className={s.chipsLabel}>See it as</span>
        {VIEWERS.map((v) => (
          <button key={v} type="button" className={s.chip} aria-pressed={v === meId} onClick={() => setMeId(v)}>
            {names.get(v)}
          </button>
        ))}
      </div>
      {!compact && <p className={s.live} aria-live="polite">
        Seen by {meName}: Thomas is {meId === 'thomas' ? 'you' : `their ${label('thomas').toLowerCase()}`}.
      </p>}
    </div>
  );
}

const STEPS = [
  { n: '1', title: 'Plant the first branch', body: 'Add yourself, your parents, your partner. Portraits, birthplaces and stories go on each person’s card.' },
  { n: '2', title: 'Send one link', body: 'Drop one invite link in the family chat, or pre-link a link to a profile so the right person lands in the right place.' },
  { n: '3', title: 'Everyone finds themselves', body: 'Each relative signs in, picks their card or adds themselves, and sees the tree from their own place, tagged “You”, with every label from their point of view.' },
];

const FEATURES = [
  { icon: 'compass', title: 'Labels from your seat', body: 'Father, mother-in-law, niece, second cousin, cousin’s daughter. Step-family and co-parents-in-law included, worked out for whoever is looking.' },
  { icon: 'photo', title: 'Faces and stories', body: 'Square-cropped portraits, life details, and the story behind a person, the part a spreadsheet of names always loses.' },
  { icon: 'lock', title: 'Private, always', body: 'Invite-only. Owners, editors and viewers. No public search, no DNA matching, no strangers browsing your relatives.' },
  { icon: 'undo', title: 'Edit history and undo', body: 'Every change is logged with who made it and when. Slip of the finger? Revert it in a tap.' },
  { icon: 'pan', title: 'A tree that moves', body: 'Pan, pinch and zoom a clean vertical canvas, elders on top. Select anyone and their line lights up in gold.' },
  { icon: 'box', title: 'Your data is yours', body: 'Export the whole tree with its photos whenever you like, and import a backup just as easily.' },
] as const;

const FAQ = [
  { q: 'Is Lineage free?', a: 'Yes. There are no ads and no subscriptions, and your family’s details are never sold or used for targeting.' },
  { q: 'Who can see my tree?', a: 'Only people you invite. Every tree has one owner, any number of editors who add their own cards and can change only those, and viewers who simply explore. Nothing is public or searchable.' },
  { q: 'Do my relatives need to be technical?', a: 'No. An invite opens a page where they sign in with Google or an email and password, then land on the tree. If they aren’t on it yet, they add themselves in a minute. That’s the whole setup.' },
  { q: 'Is there an app?', a: 'Lineage works in any modern browser on phone or desktop today. Native iOS and Android apps are on the way, built from the same code so the tree looks identical everywhere.' },
  { q: 'Can I bring an existing tree or take mine with me?', a: 'Yes. Import a Lineage backup (photos included), and export your tree and photos at any time.' },
];

function Icon({ name }: { name: (typeof FEATURES)[number]['icon'] }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true } as const;
  switch (name) {
    case 'compass':
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5z" /></svg>);
    case 'photo':
      return (<svg {...common}><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m21 16-5-5-9 9" /></svg>);
    case 'lock':
      return (<svg {...common}><rect x="5" y="11" width="14" height="9" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>);
    case 'undo':
      return (<svg {...common}><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></svg>);
    case 'pan':
      return (<svg {...common}><path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" /></svg>);
    case 'box':
      return (<svg {...common}><path d="M21 8 12 3 3 8v8l9 5 9-5z" /><path d="m3 8 9 5 9-5M12 13v8" /></svg>);
  }
}

export function Landing() {
  const root = useReveal();
  return (
    <div className={s.page} ref={root}>
      <header className={s.nav}>
        <Link to="/" className={s.brand} aria-label="Lineage home">
          <Mark /> <span>Lineage</span>
        </Link>
        <nav className={s.navLinks} aria-label="Primary">
          <a href="#how" className={s.hideSm}>How it works</a>
          <a href="#features" className={s.hideSm}>Features</a>
          <a href="#faq" className={s.hideSm}>FAQ</a>
          <Link to="/sign-in" search={{ redirect: undefined }} className="btn btn-ghost btn-sm">Sign in</Link>
        </nav>
      </header>

      <main>
        <section className={s.hero}>
          <div className={s.heroCopy}>
            <span className={s.badge}>A private family tree · No ads · Free</span>
            <h1 className={s.h1}>Your family, as it <em>really</em> connects.</h1>
            <p className={s.lede}>
              Lineage is a beautiful, private tree where every relative sees how they’re related to everyone else. Open it, and the whole family arranges itself around you.
            </p>
            <div className={s.ctas}>
              <Link to="/sign-in" search={{ redirect: undefined }} className="btn btn-primary" style={{ minHeight: 52, padding: '0 26px', fontSize: 16 }}>Start your tree</Link>
              <Link to="/demo" className="btn" style={{ minHeight: 52, padding: '0 22px', fontSize: 16 }}>Explore a sample family</Link>
            </div>
            <p className={s.fine}>Sign in with Google or email. No credit card, nothing to install.</p>
          </div>
          <LiveTree />
        </section>

        <section className={s.band} aria-label="At a glance">
          <ul className={s.glance}>
            <li><strong>60 seconds</strong><span>from invite to seeing the tree</span></li>
            <li><strong>One link</strong><span>is all a relative needs</span></li>
            <li><strong>Zero ads</strong><span>and nothing public</span></li>
            <li><strong>Any screen</strong><span>phone, tablet or desktop</span></li>
          </ul>
        </section>

        <section id="how" className={s.section}>
          <div className={s.head} data-reveal>
            <span className="eyebrow">How it works</span>
            <h2 className={s.h2}>From one person to the whole family in an afternoon</h2>
          </div>
          <ol className={s.steps}>
            {STEPS.map((st) => (
              <li key={st.n} className={s.step} data-reveal>
                <span className={s.stepN}>{st.n}</span>
                <h3 className={s.h3}>{st.title}</h3>
                <p className="muted">{st.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="features" className={s.section}>
          <div className={s.head} data-reveal>
            <span className="eyebrow">Made for families, not researchers</span>
            <h2 className={s.h2}>The warmth of an heirloom, the ease of an app</h2>
            <p className={`muted ${s.sub}`}>Genealogy tools are dense, ad-heavy and built for strangers. Lineage is built for the people who actually share a dinner table.</p>
          </div>
          <div className={s.grid}>
            {FEATURES.map((f) => (
              <article key={f.title} className={s.feature} data-reveal>
                <span className={s.icon}><Icon name={f.icon} /></span>
                <h3 className={s.h3}>{f.title}</h3>
                <p className="muted">{f.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={`${s.section} ${s.split}`}>
          <div data-reveal>
            <span className="eyebrow">For every generation</span>
            <h2 className={s.h2}>Easy enough for grandma. Powerful enough for the keeper.</h2>
          </div>
          <div className={s.roles}>
            <div className={s.role} data-reveal>
              <h3 className={s.h3}>The keeper</h3>
              <p className="muted">Builds the tree, adds photos, fixes mistakes and invites everyone. Full control, with undo for every change.</p>
            </div>
            <div className={s.role} data-reveal>
              <h3 className={s.h3}>The contributor</h3>
              <p className="muted">A sibling or cousin you trust to add their own branch and photos. They can add new cards but only change the ones they created, never anyone else’s.</p>
            </div>
            <div className={s.role} data-reveal>
              <h3 className={s.h3}>The elder</h3>
              <p className="muted">Opens an invite on their phone, signs in once and sees the tree, finding their card or adding themselves. Large cards, clear labels, nothing to learn.</p>
            </div>
          </div>
        </section>

        <section className={s.privacy} data-reveal>
          <span className="eyebrow">Private by design</span>
          <h2 className={s.h2}>Your family isn’t a data set.</h2>
          <p className={s.privacyBody}>
            No ads. No public search. No DNA matching. No selling what you share. Lineage exists to keep your family close, so a tree is visible only to the people you invite.
          </p>
          <div className={s.pills}>
            <span className="pill">Invite only</span>
            <span className="pill">Roles you control</span>
            <span className="pill">Export anytime</span>
            <span className="pill">Delete account in-app</span>
          </div>
        </section>

        <section id="faq" className={s.section}>
          <div className={s.head} data-reveal>
            <span className="eyebrow">Questions</span>
            <h2 className={s.h2}>Good to know</h2>
          </div>
          <div className={s.faq}>
            {FAQ.map((f) => (
              <details key={f.q} className={s.qa} data-reveal>
                <summary>{f.q}</summary>
                <p className="muted">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={s.final} data-reveal>
          <h2 className={s.h2}>Start with you. The rest of the family follows.</h2>
          <p className={s.sub}>It takes a minute to begin, and the stories will outlast all of us.</p>
          <div className={`${s.ctas} ${s.ctasCenter}`}>
            <Link to="/sign-in" search={{ redirect: undefined }} className="btn btn-primary" style={{ minHeight: 52, padding: '0 28px', fontSize: 16 }}>Start your tree</Link>
            <Link to="/demo" className="btn" style={{ minHeight: 52, padding: '0 22px', fontSize: 16 }}>Try the sample family</Link>
          </div>
        </section>
      </main>

      <footer className={s.footer}>
        <span className={s.brand}><Mark size={18} /> <span>Lineage</span></span>
        <nav className={s.footLinks} aria-label="Footer">
          <Link to="/demo">Sample family</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/delete-account">Delete account</Link>
          <Link to="/sign-in" search={{ redirect: undefined }}>Sign in</Link>
        </nav>
      </footer>
    </div>
  );
}
